import type { Bounds } from "context/PlotMap/geo";

/** Mapbox image source corners: top-left, top-right, bottom-right, bottom-left. */
export type ImageCornerCoordinates = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

export interface OrthoImageOverlay {
  bounds: Bounds;
  coordinates: ImageCornerCoordinates;
  imageUrl: string;
  outlineGeoJson: ReturnType<typeof toOutlineGeoJson>;
}

export interface OrthoDetailTile {
  coordinates: ImageCornerCoordinates;
  file: string;
  id: string;
  imageUrl: string;
}

/** Enter full-res tiles at/above this zoom; exit slightly lower for hysteresis. */
export const DETAIL_ZOOM_ENTER = 19.5;
export const DETAIL_ZOOM_EXIT = 19;

/** Prefetch / fetch tiles slightly outside the viewport. */
export const VIEWPORT_TILE_BUFFER_RATIO = 0.15;

/**
 * Keep already-mounted detail sources in a wider band so pan/zoom does not
 * unmount them (which flashes the low-res overview underneath).
 */
export const VIEWPORT_TILE_RETAIN_RATIO = 0.75;

export const toOutlineGeoJson = (coordinates: ImageCornerCoordinates) => ({
  type: "FeatureCollection" as const,
  features: [
    {
      type: "Feature" as const,
      geometry: {
        type: "LineString" as const,
        coordinates: [...coordinates, coordinates[0]],
      },
      properties: {},
    },
  ],
});

export const getBoundsFromImageCorners = (
  coordinates: ImageCornerCoordinates,
): Bounds => {
  const lngs = coordinates.map(([lng]) => lng);
  const lats = coordinates.map(([, lat]) => lat);

  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

export const createOrthoImageOverlay = (
  coordinates: ImageCornerCoordinates,
  imageUrl: string,
): OrthoImageOverlay => ({
  bounds: getBoundsFromImageCorners(coordinates),
  coordinates,
  imageUrl,
  outlineGeoJson: toOutlineGeoJson(coordinates),
});

export interface LngLatBoundsLike {
  getEast: () => number;
  getNorth: () => number;
  getSouth: () => number;
  getWest: () => number;
}

export const preloadImageUrls = async (urls: string[]): Promise<void> => {
  await Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            resolve();
          };
          image.onerror = () => {
            reject(new Error(`Failed to preload ortho image: ${url}`));
          };
          image.src = url;
        }),
    ),
  );
};
